<template>
  <div>
    <StackedHorizontalBarChart
      :title="chartTitle"
      :filterContext="filterContext"
      :dataProviders="dataProviders"
      :contextUpdaters="contextUpdaters"
      :navigationConfig="navigationConfig"
      :chartFontColor="chartFontColor"
      :labelWidth="160"
    />
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import StackedHorizontalBarChart from '@/components/charts/base/StackedHorizontalBarChart.vue';
import { executeAndProcessQuery } from '@/utils/stackedBarData';
import { sqlLiteral } from '@/utils/assetFields';

const store = useStore();
const { t } = useI18n();
const filterContext = computed(() => store.state.filterContext);
const chartFontColor = computed(() => store.getters.chartFontColor);
const conn = store.getters.getDBConnection;

const chartName = 'drawingLocationChart';
const chartTitle = t('drawingBreakdown');

// Unique assets per drawing, then per location within the drawing. Ids are the
// drawing/location ids rather than titles, so two rooms sharing a name on
// different drawings stay distinct.
const drawingLevel = (table) => `
  SELECT
    d.Id AS id,
    d.Title AS title,
    s.Title AS status_title,
    s.Color AS color,
    COUNT(*) AS value,
    MIN(s.Sort) AS sort
  FROM ${table} a
  JOIN locations l ON l.Id = a.assetLocationId
  JOIN drawings d ON d.Id = l.DrawingId
  JOIN status_templates s ON a.assetStatusId = s.Id
  GROUP BY d.Id, d.Title, s.Title, s.Color
  ORDER BY d.Title, sort, status_title
`;

const locationLevel = (table) => `
  SELECT
    l.Id AS id,
    l.Title AS title,
    d.Id AS parentId,
    s.Title AS status_title,
    s.Color AS color,
    COUNT(*) AS value,
    MIN(s.Sort) AS sort
  FROM ${table} a
  JOIN locations l ON l.Id = a.assetLocationId
  JOIN drawings d ON d.Id = l.DrawingId
  JOIN status_templates s ON a.assetStatusId = s.Id
  GROUP BY l.Id, l.Title, d.Id, s.Title, s.Color
  ORDER BY l.Title, sort, status_title
`;

const table = (filtered) => (filtered ? 'filteredAssets' : 'unique_assets');

const dataProviders = {
  drawing: (filtered = true) => executeAndProcessQuery(conn, drawingLevel(table(filtered))),
  location: (filtered = true) => executeAndProcessQuery(conn, locationLevel(table(filtered))),
};

const lookupTitle = async (sql) => {
  const result = await conn.query(sql);
  const row = result.toArray()[0];
  return row ? row.toJSON().title : null;
};

const contextUpdaters = {
  drawing: async (drawingId) => {
    const title = await lookupTitle(`SELECT Title AS title FROM drawings WHERE Id = ${sqlLiteral(drawingId)}`);
    await filterContext.value.updateFilterWithCondition(
      chartName,
      `assetLocationId IN (SELECT Id FROM locations WHERE DrawingId = ${sqlLiteral(drawingId)})`,
      `${t('drawing')}: ${title || drawingId}`
    );
  },
  location: async (locationId) => {
    const title = await lookupTitle(`SELECT Title AS title FROM locations WHERE Id = ${sqlLiteral(locationId)}`);
    await filterContext.value.updateFilterWithCondition(
      chartName,
      `assetLocationId = ${sqlLiteral(locationId)}`,
      `${t('location')}: ${title || locationId}`
    );
  },
  reset: async () => { await filterContext.value.removeFilter(chartName); },
};

const navigationConfig = [
  { level: 'drawing', titleKey: 'drawing' },
  { level: 'location', titleKey: 'location' },
];
</script>
